import React, { useState, useEffect } from 'react';
import {
   Briefcase, MessageSquare, Clock, CheckCircle2,
   ArrowUpRight, Target, ClipboardList, AlertCircle,
   Send, User, Building2, Zap, Phone, Mail, Calendar,
   MoreHorizontal, ChevronRight, Filter, Search, Plus,
   Sparkles, ShieldCheck, RefreshCw, BarChart2,
   AlertTriangle, Lock, Unlock, Grid, List, Activity, UserPlus, Timer, Linkedin,
   Users, FileCheck, Video, Loader2, X, Trash2, ChevronLeft
} from 'lucide-react';
import { Vacancy, SupportRequest, RecruiterTask, RecruiterProfile, SLAStatus } from '../types';
import { googleCalendarSync } from '../services/calendarService';
import { getCalendarEvents } from '../services/microsoftGraphService';
import { MOCK_CLIENTS } from '../constants';

interface RecruiterWorkspaceProps {
   vacancies?: Vacancy[];
   onNavigate?: (tab: string, context?: any) => void;
}

const MOCK_RECRUITER_PROFILE: RecruiterProfile = {
   capacity: { current: 3, max: 5 },
   skills: ['IT', 'SaaS', 'Sales'],
   performance: {
      placementRate: 88,
      avgDaysToFill: 24,
      slaCompliance: 96
   },
   status: 'Available'
};

const MOCK_TASKS: RecruiterTask[] = [
   { id: 't1', title: 'Screening 3 candidates', deadline: 'Today', completed: false, priority: 'High', slaStatus: 'Warning', clientName: 'TechNova', vacancyId: 'v1' },
   { id: 't2', title: 'Feedback call hiring manager', deadline: 'Tomorrow', completed: false, priority: 'Medium', slaStatus: 'OnTrack', clientName: 'GreenBuild', vacancyId: 'v2' },
   { id: 't3', title: 'Contract proposal drafting', deadline: 'Yesterday', completed: false, priority: 'High', slaStatus: 'Breach', clientName: 'TechNova', vacancyId: 'v1' }
];

import { supabase, supabase_get_recent_communications_global, supabase_log_interaction } from '../services/supabaseService';
import TodoDetailModal from './TodoDetailModal';
import TeamsMeetingScheduler from './TeamsMeetingScheduler';
import WeekViewCalendar from './WeekViewCalendar';
import AiRichTextEditor from './AiRichTextEditor';
import BulkEmailComposerModal, { BulkCandidate } from './BulkEmailComposerModal';
import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';

// ... (keep props and other mocks)

import PremiumChannelsDashboard from './PremiumChannelsDashboard';

const RecruiterWorkspace: React.FC<RecruiterWorkspaceProps> = ({ vacancies = [], onNavigate }) => {
   const [activeTab, setActiveTab] = useState<'dashboard' | 'marketplace' | 'workbench' | 'insights'>('dashboard');
   const [recruiter, setRecruiter] = useState<RecruiterProfile>(MOCK_RECRUITER_PROFILE);
   const [myTasks, setMyTasks] = useState<any[]>([]); // Real tasks
   const [selectedTodoId, setSelectedTodoId] = useState<string | null>(null);
   const [loadingTasks, setLoadingTasks] = useState(false);
   const [searchTerm, setSearchTerm] = useState('');
   const [filterService, setFilterService] = useState('All');
   const [showClientContact, setShowClientContact] = useState<string | null>(null);
   const [currentUser, setCurrentUser] = useState<any>(null);
   const [isAdmin, setIsAdmin] = useState(false);
   const [colleagues, setColleagues] = useState<any[]>([]);
   const [assigningVacancyId, setAssigningVacancyId] = useState<string | null>(null);
   const [dashboardTab, setDashboardTab] = useState<'Overzicht' | 'Premium Kanalen' | 'Agenda' | 'Taken' | 'Activiteiten' | 'Postvak UIT' | 'Verzonden'>('Overzicht');
   const [recentCandidates, setRecentCandidates] = useState<any[]>([]);
   const [recentVacancies, setRecentVacancies] = useState<any[]>([]);
   const [recentActivities, setRecentActivities] = useState<any[]>([]);
   const [outboxPage, setOutboxPage] = useState(1);
   const [selectedOutboxIds, setSelectedOutboxIds] = useState<string[]>([]);
   const [outboxSort, setOutboxSort] = useState<{column: 'type' | 'title' | 'date', direction: 'asc'|'desc'}>({column: 'date', direction: 'desc'});
   const [sentPage, setSentPage] = useState(1);
   const [sentSort, setSentSort] = useState<{column: 'type' | 'title' | 'date', direction: 'asc'|'desc'}>({column: 'date', direction: 'desc'});
   const [outboxModalState, setOutboxModalState] = useState<{isOpen: boolean, type: 'email'|'whatsapp'|null, candidate: BulkCandidate | null, todoId: string | null, initialBody: string}>({isOpen: false, type: null, candidate: null, todoId: null, initialBody: ''});
   const handleBulkSend = async () => {
       if (selectedOutboxIds.length === 0) return;
       if (confirm(`Weet je zeker dat je ${selectedOutboxIds.length} berichten wilt verzenden?`)) {
           await supabase.from('todos').update({ status: 'Completed', updated_at: new Date().toISOString() }).in('id', selectedOutboxIds);
           setSelectedOutboxIds([]);
           fetchUserAndTasks();
           alert('Berichten succesvol gemarkeerd als verzonden!');
       }
   };
   const handleDeleteOutboxTodo = async (id: string, e: React.MouseEvent) => {
       e.stopPropagation();
       if (confirm('Bevestig verwijderen van dit concept?')) {
           await supabase.from('todos').delete().eq('id', id);
           fetchUserAndTasks();
       }
   };
   const handleOpenOutboxTodo = async (t: any, isWhatsApp: boolean) => {
       let candArray: BulkCandidate = { id: t.candidate_id || '', name: 'Onbekend', email: '', phone: '' };
       if (t.candidate_id) {
           const { data: cand } = await supabase.from('candidates').select('name, email, phone').eq('id', t.candidate_id).single();
           if (cand) candArray = { id: t.candidate_id, name: cand.name || 'Onbekend', email: cand.email || '', phone: cand.phone || '' };
       }
       setOutboxModalState({
           isOpen: true,
           type: isWhatsApp ? 'whatsapp' : 'email',
           candidate: candArray,
           todoId: t.id,
           initialBody: t.description || ''
       });
   };
   const [outboxSearchQuery, setOutboxSearchQuery] = useState('');
   const [outboxDateFilter, setOutboxDateFilter] = useState('');
   const [sentSearchQuery, setSentSearchQuery] = useState('');
   const [sentDateFilter, setSentDateFilter] = useState('');
   const [appointments, setAppointments] = useState<any[]>([]);
   const [loadingDashboard, setLoadingDashboard] = useState(false);
   const [appointmentFilter, setAppointmentFilter] = useState<'Deze week' | 'Vandaag' | 'Afgelopen afspraken' | 'Specifieke datum'>('Deze week');
   const [selectedDate, setSelectedDate] = useState<Date | null>(null);
   const [currentMonthDate, setCurrentMonthDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
   const [showTeamsModal, setShowTeamsModal] = useState(false);

   const availableVacancies = vacancies.filter(v => (v.status === ('Draft' as any) || v.status === ('Ready' as any) || v.status === ('Live_AB' as any)) && !v.assignedRecruiterId);
   const myAssignments = vacancies.filter(v =>
      v.assignedRecruiterId === currentUser?.id || (isAdmin && v.assignedRecruiterId)
   );

   const handleClaim = async (id: string) => {
      if (!currentUser) return;
      try {
         await supabase.from('vacancies').update({ assigned_recruiter_id: currentUser.id }).eq('id', id);
         if (onNavigate) {
            // Optimistic refresh handled by parent if possible, or trigger reload
            window.location.reload();
         }
      } catch (e) { console.error('Error claiming', e); }
   };

   const handleAction = (action: string, id: string, client: any) => {
      if (action === 'contact') {
         setShowClientContact(showClientContact === id ? null : id);
      } else {
         console.log(`Action ${action} on ${id}`);
      }
   };

   // ... (other state)

   useEffect(() => {
      fetchUserAndTasks();
   }, []);

   const fetchUserAndTasks = async () => {
      setLoadingTasks(true);
      try {
         const { data: { user } } = await supabase.auth.getUser();
         if (!user) return;
         setCurrentUser(user);

         // Check if user is admin
         const { data: profile } = await supabase.from('profiles').select('role, client_id').eq('id', user.id).single();
         const userIsAdmin = profile?.role === 'ADMIN' || profile?.role === 'CLIENT';
         setIsAdmin(userIsAdmin);

         if (userIsAdmin && profile?.client_id) {
            const { data: team } = await supabase.from('profiles').select('id, name, email').eq('client_id', profile.client_id);
            setColleagues(team || []);
         }

         let query = supabase.from('todos').select('*, candidates(name), vacancies(title, opdrachtgever_id, eindklant_id, client_id, opdrachtgever:clients!vacancies_opdrachtgever_id_fkey(name), eindklant:clients!vacancies_eindklant_id_fkey(name), client:clients!vacancies_client_id_fkey(name))').order('due_date', { ascending: true });

         if (!userIsAdmin) {
            query = query.or(`assignee_id.eq.${user.id},user_id.eq.${user.id}`);
         }

         const { data, error } = await query;
         if (error) throw error;
         setMyTasks(data || []);

         // Fetch recent candidates
         let candidateQuery = supabase.from('candidates').select('id, added_at, name, role, photo_url').order('added_at', { ascending: false }).limit(5);
         if (userIsAdmin && profile?.client_id) {
            candidateQuery = candidateQuery.eq('client_id', profile.client_id);
         }
         const { data: candsData } = await candidateQuery;
         if (candsData) {
             const cands = candsData.filter((c: any) => c.name);
             setRecentCandidates(cands);
         }

         // Fetch recent vacancies
         let vacancyQuery = supabase.from('vacancies').select('id, title, status, created_at').order('created_at', { ascending: false }).limit(5);
         if (!userIsAdmin) {
            vacancyQuery = vacancyQuery.eq('assigned_recruiter_id', user.id);
         } else if (profile?.client_id) {
            vacancyQuery = vacancyQuery.eq('client_id', profile.client_id);
         }
         const { data: vacs } = await vacancyQuery;
         if (vacs) setRecentVacancies(vacs);

         // Fetch recent activities global
         const globalComms = await supabase_get_recent_communications_global(
            profile?.client_id,
            !userIsAdmin ? user.id : undefined,
            15
         );
         setRecentActivities(globalComms);

         // Fetch MS Graph events AND Supabase Interviews
         const calendarStart = new Date();
         calendarStart.setHours(0, 0, 0, 0);
         const calendarEnd = new Date(calendarStart.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days

         // Get MS Graph Events
         const { events } = await getCalendarEvents(calendarStart.toISOString(), calendarEnd.toISOString());

         // Format Microsoft graph events
         const msEvents = (events || []).map(e => ({
            id: e.id,
            // Subject is usually "Interview: Candidate Name" or something similar
            name: e.subject?.replace('Interview: ', '') || 'Onbekend',
            role: 'Kandidaat / Event',
            type: e.isOnlineMeeting ? (e.onlineMeetingProvider === 'teamsForBusiness' ? 'Microsoft Teams' : 'Online Meeting') : e.location?.displayName || 'Geen locatie',
            day: new Date(e.start.dateTime),
            timeStart: new Date(e.start.dateTime),
            timeEnd: new Date(e.end.dateTime),
            initials: (e.subject?.replace('Interview: ', '') || 'E').substring(0, 2).toUpperCase(),
            isGraph: true,
            videoLink: e.onlineMeeting?.joinUrl || e.webLink
         }));

         // Get Supabase Interviews
         const { data: dbInterviews } = await supabase.from('interviews')
            .select(`
                id, scheduled_at, duration_minutes, location, video_link,
                candidates(id, name, role, photo_url)
            `)
            .gte('scheduled_at', calendarStart.toISOString())
            .lte('scheduled_at', calendarEnd.toISOString());

         const supEvents = (dbInterviews || []).map(i => {
            const start = new Date(i.scheduled_at);
            const end = new Date(start.getTime() + (i.duration_minutes || 45) * 60000);
            const cand = Array.isArray(i.candidates) ? i.candidates[0] : i.candidates;
            return {
               id: i.id,
               name: cand?.name || 'Onbekend',
               role: cand?.role || 'Kandidaat',
               type: i.location || 'Interview',
               day: start,
               timeStart: start,
               timeEnd: end,
               initials: (cand?.name || 'C').substring(0, 2).toUpperCase(),
               avatar: cand?.photo_url,
               isGraph: false,
               videoLink: i.video_link
            };
         });

         // Merge and sort
         const merged = [...supEvents, ...msEvents].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
         setAppointments(merged);

      } catch (e) {
         console.error("Error fetching tasks", e);
      } finally {
         setLoadingTasks(false);
      }
   };

   // Helper for formatting time
   const formatTime = (date: Date) => date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', hour12: false });
   const formatDay = (date: Date) => {
      const today = new Date();
      if (date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
         return 'Vandaag';
      }
      return date.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
   };

   // ... (claim logic)

   const handleCompleteTask = async (id: string, currentStatus: string) => {
      const newStatus = currentStatus === 'Completed' ? 'Pending' : 'Completed';
      // Optimistic update
      setMyTasks(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));

      const { error } = await supabase.from('todos').update({ status: newStatus }).eq('id', id);
      if (error) fetchUserAndTasks(); // Revert on error
   };

   const handleChangePriority = async (id: string, newPriority: string) => {
      // Optimistic update
      setMyTasks(prev => prev.map(t => t.id === id ? { ...t, priority: newPriority } : t));

      const { error } = await supabase.from('todos').update({ priority: newPriority }).eq('id', id);
      if (error) fetchUserAndTasks(); // Revert on error
   };

   // ... (helper functions)

   return (
      <div className="flex flex-col h-[calc(100vh-80px)] bg-[#F8FAFC]">
         {/* ... (Header & Sidebar remain same) ... */}

         {/* 3. Main Content Area */}
         <div className="flex-1 bg-[#F8FAFC] overflow-y-auto custom-scrollbar p-4 2xl:p-8">

            {/* DASHBOARD VIEW (Nieuwe Layout) */}
            {activeTab === 'dashboard' && (
               <div className="space-y-6 2xl:space-y-8 animate-fadeIn max-w-[1000px] 2xl:max-w-[1200px] mx-auto">

                  {/* Sub-nav */}
                  <div className="flex items-center gap-6 border-b border-slate-100 px-2 mb-6">
                     {(['Overzicht', 'Premium Kanalen', 'Agenda', 'Taken', 'Activiteiten', 'Postvak UIT', 'Verzonden'] as const).map((tab) => (
                        <button
                           key={tab}
                           onClick={() => setDashboardTab(tab)}
                           className={`py-4 text-sm font-bold transition-all relative ${dashboardTab === tab ? 'text-indigo-900' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                           {tab}
                           {dashboardTab === tab && <div className="absolute bottom-0 left-0 w-full h-[3px] bg-indigo-600 rounded-t-full"></div>}
                           {tab === 'Taken' && <span className="ml-2 bg-indigo-600 text-white text-[10px] px-2 py-0.5 rounded-full font-black">{myTasks.filter(t => t.status !== 'Completed' && !t.title?.includes('[OUTBOX]')).length}</span>}
                           {tab === 'Postvak UIT' && <span className="ml-2 bg-rose-600 text-white text-[10px] px-2 py-0.5 rounded-full font-black">{myTasks.filter(t => t.status === 'Pending' && t.title?.includes('[OUTBOX]')).length}</span>}
                           {tab === 'Verzonden' && <span className="ml-2 bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full font-black">{myTasks.filter(t => t.status === 'Completed' && t.title?.includes('[OUTBOX]')).length}</span>}
                        </button>
                     ))}
                  </div>

                  {dashboardTab === 'Premium Kanalen' && (<div className="animate-in fade-in slide-in-from-bottom-4 duration-500"><PremiumChannelsDashboard /></div>)}
                        {dashboardTab === 'Overzicht' && (
                     <>
                        {/* Greeting & Recent Widgets */}
                        <div className="mb-6">
                           <div className="flex justify-between items-center mb-4">
                              <h2 className="text-[13px] 2xl:text-[15px] font-bold text-slate-900">Hallo {currentUser?.user_metadata?.first_name || currentUser?.user_metadata?.name?.split(' ')[0] || currentUser?.email?.split('@')[0] || 'Recruiter'} 👋 je hebt onlangs gewerkt aan:</h2>
                              <button className="flex items-center gap-1.5 text-xs 2xl:text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors">
                                 <Grid size={14} /> Widgets bewerken
                              </button>
                           </div>

                           <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar">
                              {loadingTasks ? (
                                 <div className="flex gap-4">
                                    {[1, 2, 3].map(i => <div key={i} className="min-w-[180px] h-24 bg-slate-50 rounded-xl animate-pulse border border-slate-100"></div>)}
                                 </div>
                              ) : (
                                 <>
                                    {recentCandidates.map(cand => (
                                       <div key={cand.id} onClick={() => onNavigate?.('candidates', { candidateId: cand.id })} className="min-w-[180px] bg-white border border-slate-200 rounded-xl p-4 flex flex-col justify-between shadow-sm cursor-pointer hover:border-indigo-300 transition-colors">
                                          <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs mb-3 overflow-hidden">
                                             {cand.photo_url ? <img src={cand.photo_url} alt={cand.name} className="w-full h-full object-cover" /> : (cand.name || 'C').substring(0, 2).toUpperCase()}
                                          </div>
                                          <div>
                                             <p className="font-bold text-slate-900 text-[13px] truncate">{cand.name}</p>
                                             <p className="text-xs text-slate-500 font-medium mt-0.5 truncate">{cand.role || 'Kandidaat'}</p>
                                          </div>
                                       </div>
                                    ))}
                                    {recentVacancies.map(vac => (
                                       <div key={vac.id} onClick={() => onNavigate?.('vacancies', { vacancyId: vac.id })} className="min-w-[180px] bg-white border border-slate-200 rounded-xl p-4 flex flex-col justify-between shadow-sm cursor-pointer hover:border-indigo-300 transition-colors">
                                          <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center mb-3">
                                             <Briefcase size={16} className="text-teal-600" />
                                          </div>
                                          <div>
                                             <p className="font-bold text-slate-900 text-[13px] truncate">{vac.title}</p>
                                             <p className="text-[11px] text-slate-500 font-medium flex items-center gap-1.5 mt-1">
                                                <span className={`w-2 h-2 rounded-full ${vac.status === 'Live_AB' || vac.status === 'Ready' ? 'bg-emerald-500' : 'bg-amber-500'}`}></span> {vac.status === 'Live_AB' ? 'Live A/B' : vac.status}
                                             </p>
                                          </div>
                                       </div>
                                    ))}
                                    {recentCandidates.length === 0 && recentVacancies.length === 0 && (
                                       <p className="text-sm text-slate-400 py-4 px-2">Geen recente items gevonden.</p>
                                    )}
                                 </>
                              )}
                           </div>
                        </div>

                        {/* Main Two Column Area */}
                        <div className="flex flex-col lg:flex-row gap-4 2xl:gap-6">

                           {/* Left Column: Appointments */}
                           <div className="flex-1 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
                              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                                 <div className="flex gap-6 px-2">
                                    {['Deze week', 'Vandaag', 'Afgelopen afspraken'].map((filter) => (
                                       <button
                                          key={filter}
                                          onClick={() => { setAppointmentFilter(filter as any); setSelectedDate(null); }}
                                          className={`text-[13px] font-bold pb-4 -mb-4 transition-colors relative ${appointmentFilter === filter ? 'text-indigo-900 border-b-2 border-indigo-600' : 'text-slate-500 hover:text-slate-800'}`}
                                       >
                                          {filter}
                                          {filter === 'Deze week' && <span className="ml-1 bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[11px]">{appointments.length}</span>}
                                          {filter === 'Vandaag' && <span className="ml-1 bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[11px]">{appointments.filter(a => new Date(a.timeStart).toDateString() === new Date().toDateString()).length}</span>}
                                       </button>
                                    ))}
                                    {appointmentFilter === 'Specifieke datum' && selectedDate && (
                                       <button className="text-[13px] font-bold pb-4 -mb-4 transition-colors relative text-indigo-900 border-b-2 border-indigo-600">
                                          {selectedDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                                          <span className="ml-1 bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[11px]">
                                             {appointments.filter(a => new Date(a.timeStart).toDateString() === selectedDate.toDateString()).length}
                                          </span>
                                       </button>
                                    )}
                                 </div>
                                 <button className="flex items-center gap-1 text-[13px] font-bold text-slate-600 hover:text-slate-900 px-2 lg:hidden">
                                    <Filter size={14} className="text-slate-500" />
                                 </button>
                              </div>

                              <div className="p-3 2xl:p-6 space-y-4 2xl:space-y-6 flex-1 max-h-[500px] overflow-y-auto custom-scrollbar">
                                 {appointments.filter(appt => {
                                    const today = new Date().toDateString();
                                    const isToday = new Date(appt.timeStart).toDateString() === today;
                                    if (appointmentFilter === 'Specifieke datum' && selectedDate) return new Date(appt.timeStart).toDateString() === selectedDate.toDateString();
                                    if (appointmentFilter === 'Vandaag') return isToday;
                                    if (appointmentFilter === 'Afgelopen afspraken') return new Date(appt.timeStart) < new Date();
                                    return new Date(appt.timeStart) >= new Date(new Date().setHours(0, 0, 0, 0)); // Deze week (future)
                                 }).slice(0, 10).map((appt, i) => (
                                    <div key={appt.id || i} className="flex gap-4 group">
                                       <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500 font-bold overflow-hidden shrink-0 mt-1 border border-indigo-100">
                                          {appt.avatar ? <img src={appt.avatar} alt={appt.name} className="w-full h-full object-cover" /> : <User size={20} />}
                                       </div>
                                       <div className="flex-1 border-b border-slate-100 group-last:border-transparent pb-4 pr-4">
                                          <div className="flex justify-between items-start mb-0.5">
                                             <p className="text-[13px] font-medium text-slate-500 capitalize">{formatDay(appt.timeStart)} • {formatTime(appt.timeStart)} - {formatTime(appt.timeEnd)}</p>
                                          </div>
                                          <div className="flex justify-between items-center">
                                             <div>
                                                <p className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
                                                   {appt.name} <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> <span className="text-slate-400 font-medium text-[13px]">{appt.role}</span>
                                                </p>
                                                {appt.videoLink ? (
                                                   <a href={appt.videoLink} target="_blank" rel="noreferrer" className="text-[13px] text-red- hover:underline mt-0.5 flex items-center gap-2 font-medium">
                                                      <Video size={14} className="text-red-0" /> Deelnemen aan meeting
                                                   </a>
                                                ) : (
                                                   <p className="text-[13px] text-slate-500 mt-0.5 flex items-center gap-2 font-medium">
                                                      {appt.type}
                                                   </p>
                                                )}
                                             </div>
                                             <div className="flex flex-col items-end gap-1.5">
                                                <p className="text-[11px] font-medium text-slate-400">{appt.isGraph ? 'Teams Synchronisatie' : 'Interviewer'}</p>
                                                <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold text-[10px] flex items-center justify-center shrink-0">
                                                   {appt.initials}
                                                </div>
                                             </div>
                                          </div>
                                       </div>
                                    </div>
                                 ))}

                                 {appointments.length === 0 && !loadingTasks && (
                                    <div className="flex flex-col items-center justify-center py-10 text-slate-400">
                                       <Calendar size={32} className="mb-2 opacity-50" />
                                       <p className="text-sm">Geen afspraken gevonden</p>
                                    </div>
                                 )}
                                 {loadingTasks && <Loader2 className="animate-spin text-indigo-500 mx-auto my-4" />}
                              </div>
                           </div>

                           {/* Right Column: Mini Calendar */}
                           <div className="w-full lg:w-[240px] xl:w-[280px] 2xl:w-[320px] bg-white border border-slate-200 rounded-xl shadow-sm p-4 2xl:p-6 flex flex-col shrink-0">
                              <div className="flex justify-between items-center mb-6 px-2">
                                 <button onClick={() => setCurrentMonthDate(new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() - 1, 1))} className="text-slate-500 hover:text-slate-900 px-2 py-1 bg-slate-50 rounded"><ChevronRight size={16} className="rotate-180" /></button>
                                 <h3 className="font-bold text-slate-900 text-sm capitalize">{currentMonthDate.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}</h3>
                                 <button onClick={() => setCurrentMonthDate(new Date(currentMonthDate.getFullYear(), currentMonthDate.getMonth() + 1, 1))} className="text-slate-500 hover:text-slate-900 px-2 py-1 bg-slate-50 rounded"><ChevronRight size={16} /></button>
                              </div>

                              <div className="grid grid-cols-7 gap-y-4 gap-x-1 mb-8 text-center text-[13px] font-bold">
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Ma</div>
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Di</div>
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Wo</div>
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Do</div>
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Vr</div>
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Za</div>
                                 <div className="text-slate-500 text-[11px] uppercase tracking-wider">Zo</div>

                                 {(() => {
                                    const year = currentMonthDate.getFullYear();
                                    const month = currentMonthDate.getMonth();
                                    const daysInMonth = new Date(year, month + 1, 0).getDate();
                                    const firstDay = new Date(year, month, 1).getDay();
                                    const offset = firstDay === 0 ? 6 : firstDay - 1;

                                    const cells = [];
                                    const prevMonthDays = new Date(year, month, 0).getDate();

                                    // prev month trailing days
                                    for (let i = 0; i < offset; i++) {
                                       cells.push(<div key={`prev-${i}`} className="text-slate-300 py-1.5 font-semibold">{prevMonthDays - offset + i + 1}</div>);
                                    }

                                    // current month days
                                    for (let day = 1; day <= daysInMonth; day++) {
                                       const d = new Date(year, month, day);
                                       const isSelected = selectedDate && d.toDateString() === selectedDate.toDateString();
                                       const hasAppt = appointments.some(a => new Date(a.timeStart).toDateString() === d.toDateString());
                                       const isToday = new Date().toDateString() === d.toDateString();

                                       cells.push(
                                          <button
                                             key={`curr-${day}`}
                                             onClick={() => { setSelectedDate(d); setAppointmentFilter('Specifieke datum'); }}
                                             className={`py-1.5 relative hover:bg-slate-50 rounded-lg outline-none font-bold text-[10px] xl:text-[11px] 2xl:text-[13px] transition-colors ${isSelected ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500' : isToday ? 'text-indigo-600 bg-indigo-50' : hasAppt ? 'text-indigo-600' : 'text-slate-900'}`}
                                          >
                                             {day}
                                             {hasAppt && !isSelected && <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-500"></div>}
                                          </button>
                                       );
                                    }

                                    // next month leading days
                                    const totalCells = offset + daysInMonth;
                                    const remainingCells = Math.ceil(totalCells / 7) * 7 - totalCells;
                                    for (let i = 1; i <= remainingCells; i++) {
                                       cells.push(<div key={`next-${i}`} className="text-slate-300 py-1.5 font-semibold">{i}</div>);
                                    }

                                    return cells;
                                 })()}
                              </div>

                              <div className="mt-auto space-y-2 2xl:space-y-3">
                                 <button onClick={() => setDashboardTab('Agenda')} className="w-full py-2 2xl:py-3 border border-slate-200 rounded-xl text-xs 2xl:text-sm font-bold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-2 transition-colors">
                                    <Calendar size={16} className="text-slate-500" /> Volledige agenda
                                 </button>
                                 <button onClick={() => setShowTeamsModal(true)} className="w-full py-2 2xl:py-3 bg-indigo-50 text-indigo-700 rounded-xl text-xs 2xl:text-sm font-bold hover:bg-indigo-100 flex items-center justify-center gap-2 transition-colors">
                                    <Plus size={16} className="text-indigo-600" /> Afspraak plannen
                                 </button>
                              </div>
                           </div>
                        </div>
                     </>
                  )}

                  {dashboardTab === 'Agenda' && (
                     <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 min-h-[500px]">
                        <div className="flex items-center justify-between mb-8">
                           <h3 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                              <Calendar size={24} className="text-indigo-600" /> Agenda (Microsoft 365)
                           </h3>
                           <div className="flex items-center gap-3">
                              <button onClick={() => setShowTeamsModal(true)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition flex items-center gap-2">
                                 <Plus size={16} /> Een afspraak inplannen
                              </button>
                              <button onClick={fetchUserAndTasks} className="p-2 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
                                 <RefreshCw size={16} className={`text-slate-500 ${loadingTasks ? 'animate-spin' : ''}`} />
                              </button>
                           </div>
                        </div>

                        {loadingTasks ? (
                           <div className="flex flex-col items-center justify-center py-20 text-indigo-400">
                              <Loader2 size={32} className="animate-spin mb-4" />
                              <p className="text-sm font-medium">Agenda ophalen uit Microsoft 365...</p>
                           </div>
                        ) : appointments.length === 0 ? (
                           <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                              <Calendar size={48} className="text-indigo-100 mb-4" />
                              <h3 className="text-xl font-bold text-slate-800 mb-2">Geen afspraken</h3>
                              <p className="max-w-md text-center">Je hebt de komende 7 dagen geen afspraken gepland in je Outlook agenda.</p>
                           </div>
                        ) : (
                           <div className="space-y-4">
                              {appointments.map((appt, idx) => (
                                 <div key={idx} className="flex gap-6 items-start p-4 hover:bg-slate-50 rounded-xl transition-colors border border-transparent hover:border-slate-100 group relative">
                                    <div className="flex flex-col items-center min-w-[60px]">
                                       <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{appt.day.toLocaleDateString('nl-NL', { weekday: 'short' })}</span>
                                       <span className="text-2xl font-black text-slate-900">{appt.day.getDate()}</span>
                                       <span className="text-[10px] font-bold text-slate-400 uppercase">{appt.day.toLocaleDateString('nl-NL', { month: 'short' })}</span>
                                    </div>
                                    <div className="flex-1 bg-white border border-slate-200 p-4 rounded-xl shadow-sm group-hover:shadow-md transition-shadow relative overflow-hidden">
                                       {appt.isGraph && <div className="absolute top-0 left-0 w-1 h-full bg-red-0"></div>}
                                       {!appt.isGraph && <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>}

                                       <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                                          <div>
                                             <h4 className="text-base font-bold text-slate-900">{appt.name}</h4>
                                             <p className="text-xs text-slate-500 font-medium flex items-center gap-2 mt-1">
                                                <Clock size={12} />
                                                {appt.timeStart.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                                {' - '}
                                                {appt.timeEnd.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                                             </p>
                                          </div>
                                          <div className="flex items-center gap-3">
                                             <div className="bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Type</p>
                                                {appt.videoLink ? (
                                                   <a href={appt.videoLink} target="_blank" rel="noreferrer" className="text-xs font-bold text-red- hover:text-red- flex items-center gap-1">
                                                      <Video size={12} /> Microsoft Teams
                                                   </a>
                                                ) : (
                                                   <span className="text-xs font-bold text-slate-700">{appt.type}</span>
                                                )}
                                             </div>
                                             {appt.avatar ? (
                                                <img src={appt.avatar} className="w-10 h-10 rounded-full border border-slate-200 object-cover" />
                                             ) : (
                                                <div className="w-10 h-10 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                                                   <span className="text-indigo-600 font-bold text-xs">{appt.initials}</span>
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        )}
                     </div>
                  )}
                   {dashboardTab === 'Taken' && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 min-h-[500px] animate-fadeIn">
                         <div className="flex items-center justify-between mb-6">
                           <h3 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                              <CheckCircle2 size={24} className="text-indigo-600" /> Jouw openstaande taken
                           </h3>
                           <span className="text-xs font-bold bg-slate-100 text-slate-600 px-3 py-1 rounded-full uppercase tracking-widest border border-slate-200">
                              {myTasks.filter(t => t.status !== 'Completed' && !t.title?.includes('[OUTBOX]')).length} Actief
                           </span>
                        </div>
                        {myTasks.filter(t => !t.title?.includes('[OUTBOX]')).length === 0 && (
                           <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                              <CheckCircle2 size={48} className="mb-4 opacity-50" />
                              <p className="text-sm font-bold text-slate-500">Geen taken. Je bent helemaal bij!</p>
                           </div>
                        )}
                        
                        {myTasks.filter(t => t.status !== 'Completed' && !t.title?.includes('[OUTBOX]')).length > 0 && (
                           <div className="mb-8">
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Openstaande taken</h4>
                              <div className="space-y-3">
                                 {myTasks.filter(t => t.status !== 'Completed' && !t.title?.includes('[OUTBOX]')).map(t => (
                                    <div key={t.id} className="group flex items-start gap-4 p-4 border rounded-xl transition-all cursor-pointer hover:shadow-md border-slate-200 bg-white hover:border-indigo-300" onClick={() => setSelectedTodoId(t.id)}>
                                       <button onClick={(e) => { e.stopPropagation(); handleCompleteTask(t.id, t.status); }} className="mt-0.5 shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors border-slate-300 hover:border-indigo-500 text-transparent hover:text-indigo-200">
                                          <CheckCircle2 size={14} className="currentColor" />
                                       </button>
                                       <div className="flex-1 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                                          <div>
                                             <p className="font-bold text-slate-900 text-[15px]">{t.title || t.description || 'Naamloze taak'}</p>
                                             {t.description && t.title && (
                                                <p className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</p>
                                             )}
                                          </div>
                                          <div className="flex items-center gap-3 shrink-0">
                                             <select 
                                                onClick={(e) => e.stopPropagation()} 
                                                onChange={(e) => handleChangePriority(t.id, e.target.value)} 
                                                value={t.priority || 'medium'}
                                                className={`text-[10px] font-bold px-2 py-1 rounded-md border outline-none cursor-pointer uppercase tracking-widest
                                                   ${t.priority === 'high' ? 'bg-red-50 text-red-600 border-red-200' : 
                                                     t.priority === 'low' ? 'bg-slate-50 text-slate-500 border-slate-200' : 
                                                     'bg-orange-50 text-orange-600 border-orange-200'}`}
                                             >
                                                <option value="low">Laag prio</option>
                                                <option value="medium">Middel prio</option>
                                                <option value="high">Hoog prio</option>
                                             </select>
                                             {t.due_date && (
                                                <div className={`flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-md border ${new Date(t.due_date) < new Date() ? 'bg-red-50 text-red-700 border-red-100' : 'bg-slate-50 text-slate-500 border-slate-100'}`}>
                                                   <Calendar size={12} /> {new Date(t.due_date).toLocaleDateString()}
                                                </div>
                                             )}
                                             {t.assignee_id && (
                                                <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold border border-indigo-200" title="Toegewezen">
                                                   <User size={12} />
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        )}

                        {myTasks.filter(t => t.status === 'Completed' && !t.title?.includes('[OUTBOX]')).length > 0 && (
                           <div>
                              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 border-b border-slate-100 pb-2">Afgeronde taken</h4>
                              <div className="space-y-3 opacity-60">
                                 {myTasks.filter(t => t.status === 'Completed' && !t.title?.includes('[OUTBOX]')).map(t => (
                                    <div key={t.id} className="group flex items-start gap-4 p-4 border rounded-xl transition-all cursor-pointer hover:shadow-md border-slate-100 bg-slate-50" onClick={() => setSelectedTodoId(t.id)}>
                                       <button onClick={(e) => { e.stopPropagation(); handleCompleteTask(t.id, t.status); }} className="mt-0.5 shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors bg-emerald-500 border-emerald-500 text-white">
                                          <CheckCircle2 size={14} className="text-white" />
                                       </button>
                                       <div className="flex-1 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                                          <div className="line-through">
                                             <p className="font-bold text-slate-900 text-[15px]">{t.title || t.description || 'Naamloze taak'}</p>
                                             {t.description && t.title && (
                                                <p className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</p>
                                             )}
                                          </div>
                                          <div className="flex items-center gap-3 shrink-0">
                                             <select 
                                                disabled
                                                value={t.priority || 'medium'}
                                                className={`text-[10px] font-bold px-2 py-1 rounded-md border outline-none cursor-not-allowed uppercase tracking-widest opacity-70
                                                   ${t.priority === 'high' ? 'bg-red-50 text-red-600 border-red-200' : 
                                                     t.priority === 'low' ? 'bg-slate-50 text-slate-500 border-slate-200' : 
                                                     'bg-orange-50 text-orange-600 border-orange-200'}`}
                                             >
                                                <option value="low">Laag</option>
                                                <option value="medium">Middel</option>
                                                <option value="high">Hoog</option>
                                             </select>
                                             {t.due_date && (
                                                <div className="flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-md border bg-slate-50 text-slate-500 border-slate-100">
                                                   <Calendar size={12} /> {new Date(t.due_date).toLocaleDateString()}
                                                </div>
                                             )}
                                          </div>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        )}
                     </div>
                  )}
                  {dashboardTab === 'Postvak UIT' && (
                     <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 min-h-[500px] animate-fadeIn flex flex-col">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                           <div className="flex items-center gap-2">
                              <h3 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                 <Send size={24} className="text-rose-600" /> Postvak UIT (Concepten)
                              </h3>
                              <span className="text-xs font-bold bg-slate-100 text-slate-600 px-3 py-1 rounded-full uppercase tracking-widest border border-slate-200">
                                 {myTasks.filter(t => t.status === 'Pending' && t.title?.includes('[OUTBOX]')).length} Te verzenden
                              </span>
                              {selectedOutboxIds.length > 0 && (
                                 <button onClick={handleBulkSend} className="ml-4 px-4 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-bold hover:bg-rose-700 transition shadow-sm flex items-center gap-2">
                                    <Send size={14} /> {selectedOutboxIds.length} Berichten Verzenden
                                 </button>
                              )}
                           </div>
                           <div className="flex items-center gap-3">
                              <div className="relative">
                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                 <input type="text" placeholder="Zoek op naam of bericht..." value={outboxSearchQuery} onChange={e => setOutboxSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700" />
                              </div>
                              <input type="date" value={outboxDateFilter} onChange={e => setOutboxDateFilter(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer" />
                           </div>
                        </div>

                        {(() => {
                           let filteredOutbox = myTasks.filter(t => t.status === 'Pending' && t.title?.includes('[OUTBOX]'));
                           
                           if (outboxSearchQuery) {
                              const q = outboxSearchQuery.toLowerCase();
                              filteredOutbox = filteredOutbox.filter(t => 
                                 t.title?.toLowerCase().includes(q) || 
                                 t.description?.toLowerCase().includes(q)
                              );
                           }

                           if (outboxDateFilter) {
                              filteredOutbox = filteredOutbox.filter(t => {
                                 if (!t.created_at && !t.due_date) return false;
                                 const dateToUse = t.due_date || t.created_at;
                                 return dateToUse && new Date(dateToUse).toISOString().split('T')[0] === outboxDateFilter;
                              });
                           }

                           filteredOutbox.sort((a, b) => {
                              if (outboxSort.column === 'type') {
                                 const typeA = a.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 const typeB = b.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 return outboxSort.direction === 'asc' ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
                              } else if (outboxSort.column === 'title') {
                                 const titleA = a.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 const titleB = b.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 return outboxSort.direction === 'asc' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
                              } else {
                                 const dateA = new Date(a.created_at || a.due_date || 0).getTime();
                                 const dateB = new Date(b.created_at || b.due_date || 0).getTime();
                                 return outboxSort.direction === 'asc' ? dateA - dateB : dateB - dateA;
                              }
                           });

                           return (
                              <>
                                 {filteredOutbox.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-slate-400 flex-1">
                                       <Send size={48} className="mb-4 opacity-50" />
                                       <p className="text-sm font-bold text-slate-500">Geen berichten in de wachtrij.</p>
                                    </div>
                                 ) : (
                                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                                       <table className="w-full text-left text-sm text-slate-600">
                                          <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-bold border-b border-slate-200">
                                             <tr>
                                                <th className="px-5 py-4 w-[40px]">
                                                   <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" 
                                                      checked={filteredOutbox.length > 0 && selectedOutboxIds.length === filteredOutbox.length}
                                                      onChange={(e) => setSelectedOutboxIds(e.target.checked ? filteredOutbox.map(t => t.id) : [])}
                                                   />
                                                </th>
                                                <th className="px-5 py-4 w-[130px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setOutboxSort({ column: 'type', direction: outboxSort.column === 'type' && outboxSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                                   Type {outboxSort.column === 'type' && (outboxSort.direction === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th className="px-5 py-4 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setOutboxSort({ column: 'title', direction: outboxSort.column === 'title' && outboxSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                                   Onderwerp / Ontvanger {outboxSort.column === 'title' && (outboxSort.direction === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th className="px-5 py-4 w-[150px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setOutboxSort({ column: 'date', direction: outboxSort.column === 'date' && outboxSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                                   Datum {outboxSort.column === 'date' && (outboxSort.direction === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th className="px-5 py-4 text-right w-[150px]">Actie</th>
                                             </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-100">
                                             {filteredOutbox.slice((outboxPage - 1) * 10, outboxPage * 10).map(t => {
                                                  const title = t.title?.replace('[OUTBOX]', '')?.trim() || '';
                                                  const isWhatsApp = title.toLowerCase().includes('whatsapp');
                                                  const isEmail = title.toLowerCase().includes('email') || title.toLowerCase().includes('mail');
                                                  const werkplekName = t.vacancies?.opdrachtgever?.name || t.vacancies?.eindklant?.name || t.vacancies?.client?.name || '';
                                                  
                                                  return (
                                                     <tr key={t.id} className="hover:bg-slate-50/80 transition-colors group cursor-pointer" onClick={() => handleOpenOutboxTodo(t, isWhatsApp)}>
                                                        <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                                                           <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" 
                                                              checked={selectedOutboxIds.includes(t.id)}
                                                              onChange={(e) => {
                                                                  if (e.target.checked) setSelectedOutboxIds(prev => [...prev, t.id]);
                                                                  else setSelectedOutboxIds(prev => prev.filter(id => id !== t.id));
                                                              }}
                                                           />
                                                        </td>
                                                        <td className="px-5 py-4">
                                                           {isWhatsApp ? (
                                                              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded-md w-fit text-[11px] font-black tracking-wide uppercase border border-emerald-200/50">
                                                                 <MessageSquare size={13} strokeWidth={2.5} /> WhatsApp
                                                              </div>
                                                           ) : isEmail ? (
                                                              <div className="flex items-center gap-2 text-indigo-700 bg-indigo-50 px-2.5 py-1.5 rounded-md w-fit text-[11px] font-black tracking-wide uppercase border border-indigo-200/50">
                                                                 <Mail size={13} strokeWidth={2.5} /> Email
                                                              </div>
                                                           ) : (
                                                              <div className="flex items-center gap-2 text-slate-700 bg-slate-100 px-2.5 py-1.5 rounded-md w-fit text-[11px] font-black tracking-wide uppercase border border-slate-200/50">
                                                                 <Send size={13} strokeWidth={2.5} /> Bericht
                                                              </div>
                                                           )}
                                                        </td>
                                                        <td className="px-5 py-4 min-w-0">
                                                           <div className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">{title || 'Zonder titel'}</div>
                                                           <div className="text-xs text-slate-500 line-clamp-1">{t.description || 'Geen concept beschikbaar'}</div>
                                                            {(t.candidates?.name || t.vacancies?.title || werkplekName) && (
                                                               <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px] uppercase font-bold tracking-wider">
                                                                   {t.candidates?.name && <span className="flex items-center gap-1 text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100/50 shadow-sm"><User size={10} /> {t.candidates.name}</span>}
                                                                   {t.vacancies?.title && <span className="flex items-center gap-1 text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-100/50 shadow-sm"><Briefcase size={10} /> {t.vacancies.title}</span>}
                                                                   {werkplekName && <span className="flex items-center gap-1 text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100/50 shadow-sm"><Building2 size={10} /> {werkplekName}</span>}
                                                               </div>
                                                            )}
                                                        </td>
                                                        <td className="px-5 py-4">
                                                           <div className="text-xs font-bold text-slate-900">
                                                              {t.created_at ? new Date(t.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '-'}
                                                           </div>
                                                           <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Aangemaakt</div>
                                                        </td>
                                                        <td className="px-5 py-4 text-right">
                                                           <div className="flex -space-x-2 justify-end mr-4">
                                                              {t.candidate_id && (
                                                                 <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs ring-2 ring-white" title="Kandidaat Gekoppeld">
                                                                    <User size={14} />
                                                                 </div>
                                                              )}
                                                           </div>
                                                           <div className="flex justify-end gap-2 mt-2">
                                                              <button onClick={(e) => handleDeleteOutboxTodo(t.id, e)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Verwijderen">
                                                                  <Trash2 size={16} />
                                                              </button>
                                                           </div>
                                                        </td>
                                                     </tr>
                                                  );
                                               })}
                                            </tbody>
                                         </table>
                                         
                                         {/* Pagination */}
                                         {Math.ceil(filteredOutbox.length / 10) > 1 && (
                                             <div className="p-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                                                 <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                     Pagina {outboxPage} van {Math.ceil(filteredOutbox.length / 10)}
                                                 </span>
                                                 <div className="flex gap-2">
                                                     <button onClick={() => setOutboxPage(p => Math.max(1, p - 1))} disabled={outboxPage === 1} className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded p-1 hover:bg-white disabled:opacity-50 text-slate-400 hover:text-slate-900 transition-colors">
                                                         <ChevronLeft size={16} />
                                                     </button>
                                                     <button onClick={() => setOutboxPage(p => Math.min(Math.ceil(filteredOutbox.length / 10), p + 1))} disabled={outboxPage === Math.ceil(filteredOutbox.length / 10)} className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded p-1 hover:bg-white disabled:opacity-50 text-slate-400 hover:text-slate-900 transition-colors">
                                                         <ChevronRight size={16} />
                                                     </button>
                                                 </div>
                                             </div>
                                         )}
                                    </div>
                                 )}
                              </>
                           );
                        })()}
                      </div>
                   )}
                   {dashboardTab === 'Verzonden' && (
                     <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 min-h-[500px] animate-fadeIn flex flex-col">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                           <div className="flex items-center gap-2">
                              <h3 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                 <CheckCircle2 size={24} className="text-emerald-600" /> Verzonden Items
                              </h3>
                              <span className="text-xs font-bold bg-slate-100 text-slate-600 px-3 py-1 rounded-full uppercase tracking-widest border border-slate-200">
                                 {myTasks.filter(t => t.status === 'Completed' && t.title?.includes('[OUTBOX]')).length} Historie
                              </span>
                           </div>
                           <div className="flex items-center gap-3">
                              <div className="relative">
                                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                 <input type="text" placeholder="Zoek op naam of bericht..." value={sentSearchQuery} onChange={e => setSentSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all text-slate-700" />
                              </div>
                              <input type="date" value={sentDateFilter} onChange={e => setSentDateFilter(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all cursor-pointer" />
                           </div>
                        </div>

                        {(() => {
                           let filteredSent = myTasks.filter(t => t.status === 'Completed' && t.title?.includes('[OUTBOX]'));
                           
                           if (sentSearchQuery) {
                              const q = sentSearchQuery.toLowerCase();
                              filteredSent = filteredSent.filter(t => 
                                 t.title?.toLowerCase().includes(q) || 
                                 t.description?.toLowerCase().includes(q)
                              );
                           }

                           if (sentDateFilter) {
                              filteredSent = filteredSent.filter(t => {
                                 if (!t.created_at && !t.due_date) return false;
                                 const dateToUse = t.due_date || t.created_at;
                                 return dateToUse && new Date(dateToUse).toISOString().split('T')[0] === sentDateFilter;
                              });
                           }

                           filteredSent.sort((a, b) => {
                              if (sentSort.column === 'type') {
                                 const typeA = a.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 const typeB = b.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 return sentSort.direction === 'asc' ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
                              } else if (sentSort.column === 'title') {
                                 const titleA = a.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 const titleB = b.title?.replace('[OUTBOX]', '')?.trim().toLowerCase() || '';
                                 return sentSort.direction === 'asc' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
                              } else {
                                 const dateA = new Date(a.updated_at || a.created_at || a.due_date || 0).getTime();
                                 const dateB = new Date(b.updated_at || b.created_at || b.due_date || 0).getTime();
                                 return sentSort.direction === 'asc' ? dateA - dateB : dateB - dateA;
                              }
                           });

                           return (
                              <>
                                 {filteredSent.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 text-slate-400 flex-1">
                                       <CheckCircle2 size={48} className="mb-4 opacity-50" />
                                       <p className="text-sm font-bold text-slate-500">Geen verzonden berichten gevonden.</p>
                                    </div>
                                 ) : (
                                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                                       <table className="w-full text-left text-sm text-slate-600">
                                          <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-bold border-b border-slate-200">
                                             <tr>
                                                <th className="px-5 py-4 w-[130px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setSentSort({ column: 'type', direction: sentSort.column === 'type' && sentSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                                   Type {sentSort.column === 'type' && (sentSort.direction === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th className="px-5 py-4 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setSentSort({ column: 'title', direction: sentSort.column === 'title' && sentSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                                   Onderwerp / Ontvanger {sentSort.column === 'title' && (sentSort.direction === 'asc' ? '↑' : '↓')}
                                                </th>
                                                <th className="px-5 py-4 text-right w-[150px] cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setSentSort({ column: 'date', direction: sentSort.column === 'date' && sentSort.direction === 'asc' ? 'desc' : 'asc' })}>
                                                   Datum {sentSort.column === 'date' && (sentSort.direction === 'asc' ? '↑' : '↓')}
                                                </th>
                                             </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-100">
                                             {filteredSent.slice((sentPage - 1) * 10, sentPage * 10).map(t => {
                                                   const title = t.title?.replace('[OUTBOX]', '')?.trim() || '';
                                                   const isWhatsApp = title.toLowerCase().includes('whatsapp');
                                                   const isEmail = title.toLowerCase().includes('email') || title.toLowerCase().includes('mail');
                                                   const werkplekName = t.vacancies?.opdrachtgever?.name || t.vacancies?.eindklant?.name || t.vacancies?.client?.name || '';
                                                  
                                                  return (
                                                     <tr key={t.id} className="hover:bg-slate-50/80 transition-colors group cursor-default">
                                                        <td className="px-5 py-4">
                                                           {isWhatsApp ? (
                                                              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded-md w-fit text-[11px] font-black tracking-wide uppercase border border-emerald-200/50">
                                                                 <MessageSquare size={13} strokeWidth={2.5} /> WhatsApp
                                                              </div>
                                                           ) : isEmail ? (
                                                              <div className="flex items-center gap-2 text-indigo-700 bg-indigo-50 px-2.5 py-1.5 rounded-md w-fit text-[11px] font-black tracking-wide uppercase border border-indigo-200/50">
                                                                 <Mail size={13} strokeWidth={2.5} /> Email
                                                              </div>
                                                           ) : (
                                                              <div className="flex items-center gap-2 text-slate-700 bg-slate-100 px-2.5 py-1.5 rounded-md w-fit text-[11px] font-black tracking-wide uppercase border border-slate-200/50">
                                                                 <Send size={13} strokeWidth={2.5} /> Bericht
                                                              </div>
                                                           )}
                                                        </td>
                                                        <td className="px-5 py-4 min-w-0">
                                                           <div className="font-bold text-slate-900 text-sm mb-1 line-clamp-1">{title || 'Zonder titel'}</div>
                                                           <div className="text-xs text-slate-500 line-clamp-1 opacity-70 italic">{t.description || 'Geen tekst beschikbaar'}</div>
                                                            {(t.candidates?.name || t.vacancies?.title || werkplekName) && (
                                                               <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px] uppercase font-bold tracking-wider">
                                                                   {t.candidates?.name && <span className="flex items-center gap-1 text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100/50 shadow-sm"><User size={10} /> {t.candidates.name}</span>}
                                                                   {t.vacancies?.title && <span className="flex items-center gap-1 text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-100/50 shadow-sm"><Briefcase size={10} /> {t.vacancies.title}</span>}
                                                                   {werkplekName && <span className="flex items-center gap-1 text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100/50 shadow-sm"><Building2 size={10} /> {werkplekName}</span>}
                                                               </div>
                                                            )}
                                                        </td>
                                                        <td className="px-5 py-4 text-right">
                                                           <div className="text-xs font-bold text-slate-900 flex justify-end gap-2 items-center">
                                                              {t.candidate_id && (
                                                                 <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-[10px] ring-2 ring-white" title="Kandidaat">
                                                                    <User size={10} />
                                                                 </div>
                                                              )}
                                                              {t.updated_at || t.created_at ? new Date(t.updated_at || t.created_at || '').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '-'}
                                                           </div>
                                                           <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Verzonden</div>
                                                        </td>
                                                     </tr>
                                                  );
                                               })}
                                            </tbody>
                                         </table>
                                         
                                         {/* Pagination */}
                                         {Math.ceil(filteredSent.length / 10) > 1 && (
                                             <div className="p-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                                                 <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                     Pagina {sentPage} van {Math.ceil(filteredSent.length / 10)}
                                                 </span>
                                                 <div className="flex gap-2">
                                                     <button onClick={() => setSentPage(p => Math.max(1, p - 1))} disabled={sentPage === 1} className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded p-1 hover:bg-white disabled:opacity-50 text-slate-400 hover:text-slate-900 transition-colors">
                                                         <ChevronLeft size={16} />
                                                     </button>
                                                     <button onClick={() => setSentPage(p => Math.min(Math.ceil(filteredSent.length / 10), p + 1))} disabled={sentPage === Math.ceil(filteredSent.length / 10)} className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded p-1 hover:bg-white disabled:opacity-50 text-slate-400 hover:text-slate-900 transition-colors">
                                                         <ChevronRight size={16} />
                                                     </button>
                                                 </div>
                                             </div>
                                         )}
                                    </div>
                                 )}
                              </>
                           );
                        })()}
                     </div>
                  )}

                  {dashboardTab === 'Activiteiten' && (
                     <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 min-h-[500px]">
                        <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                           <Activity className="text-indigo-600" size={24} /> Recente Activiteiten
                        </h3>
                        {recentActivities.length === 0 ? (
                           <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                              <Activity size={48} className="mb-4 opacity-50" />
                              <p>Nog geen recente activiteiten gevonden.</p>
                           </div>
                        ) : (
                           <div className="space-y-4">
                              {recentActivities.map(activity => {
                                 let icon = <MessageSquare size={16} />;
                                 let colorClass = "bg-red- text-red-";
                                 if (activity.type === 'CALL') {
                                    icon = <Phone size={16} />;
                                    colorClass = "bg-emerald-100 text-emerald-600";
                                 } else if (activity.type === 'EMAIL') {
                                    icon = <Mail size={16} />;
                                    colorClass = "bg-indigo-100 text-indigo-600";
                                 } else if (activity.type === 'NOTE') {
                                    icon = <ClipboardList size={16} />;
                                    colorClass = "bg-amber-100 text-amber-600";
                                 }

                                 const candName = activity.name || 'Onbekend';

                                 return (
                                    <div key={activity.id} className="flex gap-4 p-4 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
                                       <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                                          {icon}
                                       </div>
                                       <div className="flex-1">
                                          <div className="flex justify-between items-start mb-1">
                                             <p className="font-bold text-slate-800 text-sm">
                                                {activity.type === 'CALL' ? 'Telefoongesprek met ' :
                                                   activity.type === 'EMAIL' ? 'E-mail naar ' :
                                                      activity.type === 'NOTE' ? 'Notitie voor ' : 'Interactie met '}
                                                <span className="text-indigo-600 cursor-pointer hover:underline" onClick={() => onNavigate?.('candidates', { candidateId: activity.candidate_id })}>
                                                   {candName}
                                                </span>
                                             </p>
                                             <span className="text-[11px] text-slate-400 font-medium whitespace-nowrap ml-4">
                                                {new Date(activity.created_at).toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                             </span>
                                          </div>

                                          {activity.content && (
                                             <p className="text-sm text-slate-600 line-clamp-2 mt-1 bg-white p-3 rounded-lg border border-slate-100 shadow-sm" dangerouslySetInnerHTML={{ __html: activity.content }} />
                                          )}

                                          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100/50">
                                             <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-black text-slate-600">
                                                {(activity.profiles?.name || 'S').substring(0, 2).toUpperCase()}
                                             </div>
                                             <span className="text-xs font-bold text-slate-500">
                                                {activity.profiles?.name || 'Systeem'}
                                             </span>
                                          </div>
                                       </div>
                                    </div>
                                 );
                              })}
                           </div>
                        )}
                     </div>
                  )}

               </div>
            )}

            {/* MARKETPLACE VIEW */}
            {activeTab === 'marketplace' && (
               <div className="space-y-4 2xl:space-y-6 animate-fadeIn">
                  <div className="bg-[#000000] rounded-[2rem] p-5 2xl:p-8 text-white relative overflow-hidden shadow-xl">
                     <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-4 2xl:gap-6">
                        <div>
                           <h2 className="text-lg 2xl:text-xl font-black uppercase tracking-tight mb-0.5 2xl:mb-2">Vacancy Marketplace</h2>
                           <p className="text-slate-400 text-[10px] 2xl:text-xs font-medium">Claim nieuwe opdrachten en vul je pijplijn.</p>
                        </div>
                        <div className="flex gap-2 2xl:gap-3 bg-white/10 p-1 rounded-xl backdrop-blur-md">
                           <div className="relative">
                              <Search className="absolute left-2.5 2xl:left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                              <input
                                 type="text"
                                 placeholder="Zoek vacature..."
                                 value={searchTerm}
                                 onChange={(e) => setSearchTerm(e.target.value)}
                                 className="bg-transparent pl-8 pr-2 py-1 2xl:pl-9 2xl:pr-4 2xl:py-2 text-[10px] 2xl:text-xs font-bold text-white placeholder:text-slate-500 outline-none w-32 2xl:w-48"
                              />
                           </div>
                           <div className="h-6 2xl:h-8 w-px bg-white/10 self-center"></div>
                           <select
                              value={filterService}
                              onChange={(e) => setFilterService(e.target.value)}
                              className="bg-transparent px-2 py-1 2xl:px-4 2xl:py-2 text-[10px] 2xl:text-xs font-bold text-white outline-none cursor-pointer"
                           >
                              <option value="All" className="text-slate-900">Alle Types</option>
                              <option value="Full" className="text-slate-900">Full Service</option>
                              <option value="Assisted" className="text-slate-900">Assisted</option>
                           </select>
                        </div>
                     </div>
                     <div className="absolute -right-10 -bottom-20 w-64 h-64 bg-[#e51b23] rounded-full blur-[80px] opacity-30"></div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                     {availableVacancies.length === 0 ? (
                        <div className="col-span-full py-20 text-center border-2 border-dashed border-slate-200 rounded-[2rem]">
                           <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">Geen beschikbare vacatures momenteel.</p>
                        </div>
                     ) : (
                        availableVacancies.map(vac => {
                           const client = (vac as any).client || MOCK_CLIENTS.find(c => c.id === vac.clientId);
                           return (
                              <div key={vac.id} className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-6 hover:shadow-xl hover:border-slate-300 transition-all group flex flex-col h-full relative overflow-hidden">
                                 {vac.data.serviceLevel === 'Full' && <div className="absolute top-0 right-0 bg-[#e51b23] text-white text-[8px] font-black px-3 py-1 rounded-bl-xl uppercase tracking-widest">High Priority</div>}

                                 <div className="flex items-center gap-4 mb-6">
                                    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 p-2">
                                       {client?.logo_url ? <img src={client.logo_url} className="w-full h-full object-contain" /> : <Building2 className="text-slate-300" />}
                                    </div>
                                    <div>
                                       <h3 className="font-black text-slate-900 text-sm uppercase tracking-tight leading-snug">{vac.title}</h3>
                                       <p className="text-xs font-bold text-slate-500 mt-0.5">{client?.name}</p>
                                    </div>
                                 </div>

                                 <div className="space-y-3 mb-8 flex-1">
                                    <div className="flex justify-between text-xs border-b border-slate-50 pb-2">
                                       <span className="text-slate-400 font-medium">Service Level</span>
                                       <span className="font-bold text-slate-700">{vac.data.serviceLevel} Service</span>
                                    </div>
                                    <div className="flex justify-between text-xs border-b border-slate-50 pb-2">
                                       <span className="text-slate-400 font-medium">Locatie</span>
                                       <span className="font-bold text-slate-700">{client?.address_city || 'Remote'}</span>
                                    </div>
                                    <div className="flex justify-between text-xs pb-2">
                                       <span className="text-slate-400 font-medium">Jouw Match</span>
                                       <span className="font-black text-emerald-600">92%</span>
                                    </div>
                                 </div>

                                 <button
                                    onClick={() => handleClaim(vac.id)}
                                    className="w-full py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#e51b23] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2"
                                 >
                                    <UserPlus size={14} /> Claim Opdracht
                                 </button>
                              </div>
                           );
                        })
                     )}
                  </div>
               </div>
            )}

            {/* WORKBENCH VIEW (Assignments) */}
            {activeTab === 'workbench' && (
               <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between">
                     <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Mijn Opdrachten</h2>
                     <div className="flex gap-2">
                        <button className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"><Filter size={16} className="text-slate-500" /></button>
                     </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                     {myAssignments.map(vac => {
                        const client = (vac as any).client || MOCK_CLIENTS.find(c => c.id === vac.clientId);
                        return (
                           <div key={vac.id} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col gap-6 hover:border-indigo-200 transition-all group">
                              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                 <div className="flex items-center gap-6 w-full md:w-auto">
                                    <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 p-3 relative">
                                       {client?.logo_url ? <img src={client.logo_url} className="w-full h-full object-contain" /> : <Building2 className="text-slate-300" />}
                                       {vac.status === 'Live_AB' && <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>}
                                    </div>
                                    <div>
                                       <div className="flex items-center gap-3">
                                          <h3 className="font-black text-lg text-slate-900">{vac.title}</h3>
                                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-black uppercase tracking-widest rounded border border-slate-200">{vac.status}</span>
                                       </div>
                                       <p className="text-sm font-bold text-slate-500 mt-1 flex items-center gap-2">
                                          <Building2 size={12} /> {client?.name}
                                          <span className="text-slate-300">•</span>
                                          <span className="text-indigo-600">3 Kandidaten in procedure</span>
                                       </p>
                                    </div>
                                 </div>

                                 <div className="flex flex-col items-end gap-2 w-full md:w-auto relative">
                                    {isAdmin && (
                                       <div className="mb-2 w-full text-right" onClick={() => setAssigningVacancyId(assigningVacancyId === vac.id ? null : vac.id)}>
                                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 cursor-pointer hover:text-red-0">Recruiter</p>
                                          <div className="text-xs font-bold text-slate-700 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 cursor-pointer flex items-center gap-2 justify-end">
                                             <User size={12} /> {colleagues.find(c => c.id === vac.assignedRecruiterId)?.name?.split(' ')[0] || 'Kies...'} <MoreHorizontal size={12} />
                                          </div>

                                          {assigningVacancyId === vac.id && (
                                             <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-2 overflow-hidden animate-fadeIn text-left">
                                                <p className="text-[10px] font-bold text-slate-400 p-2 uppercase tracking-widest border-b border-slate-100">Toewijzen aan</p>
                                                <div className="max-h-40 overflow-y-auto">
                                                   <button
                                                      onClick={async (e) => { e.stopPropagation(); setAssigningVacancyId(null); await supabase.from('vacancies').update({ assigned_recruiter_id: null }).eq('id', vac.id); window.location.reload(); }}
                                                      className="w-full text-left px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-50 transition-colors"
                                                   >-- Unassign --</button>
                                                   {colleagues.map(c => (
                                                      <button
                                                         key={c.id}
                                                         onClick={async (e) => {
                                                            e.stopPropagation();
                                                            setAssigningVacancyId(null);
                                                            await supabase.from('vacancies').update({ assigned_recruiter_id: c.id }).eq('id', vac.id);
                                                            window.location.reload();
                                                         }}
                                                         className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                                                      >
                                                         {c.name || c.email}
                                                      </button>
                                                   ))}
                                                </div>
                                             </div>
                                          )}
                                       </div>
                                    )}

                                    <div className="text-right">
                                       <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">SLA Deadline</p>
                                       <div className="flex items-center gap-2 justify-end text-xs font-bold text-slate-800 bg-red-50 px-3 py-1 rounded-full border border-red-100 text-red-700">
                                          <Timer size={12} /> 24u resterend
                                       </div>
                                    </div>
                                 </div>
                              </div>

                              {/* Action Bar */}
                              <div className="pt-6 border-t border-slate-50 flex items-center gap-3">
                                 <button onClick={() => handleAction('linkedin', vac.id, client)} className="flex-1 py-2 bg-[#0077b5]/10 text-[#0077b5] rounded-xl text-xs font-bold hover:bg-[#0077b5] hover:text-white transition-all flex items-center justify-center gap-2">
                                    <Linkedin size={14} /> LinkedIn Post
                                 </button>
                                 <button onClick={() => handleAction('search', vac.id, client)} className="flex-1 py-2 bg-purple-50 text-purple-600 rounded-xl text-xs font-bold hover:bg-purple-500 hover:text-white transition-all flex items-center justify-center gap-2">
                                    <Search size={14} /> Zoek Kandidaten
                                 </button>
                                 <button onClick={() => handleAction('interviews', vac.id, client)} className="flex-1 py-2 bg-red- text-red- rounded-xl text-xs font-bold hover:bg-red-0 hover:text-white transition-all flex items-center justify-center gap-2">
                                    <MessageSquare size={14} /> Check Interviews
                                 </button>
                                 <div className="relative">
                                    <button onClick={() => handleAction('contact', vac.id, client)} className={`h-full px-4 py-2 border border-slate-200 rounded-xl text-slate-500 hover:text-slate-900 hover:border-slate-300 transition-all ${showClientContact === vac.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white'}`}>
                                       <Phone size={14} />
                                    </button>
                                    {showClientContact === vac.id && (
                                       <div className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-10 animate-in zoom-in-95 duration-200">
                                          <h4 className="font-bold text-slate-900 text-xs uppercase mb-2">Contactpersoon</h4>
                                          <div className="flex items-center gap-3 mb-3">
                                             <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500"><User size={14} /></div>
                                             <div>
                                                <p className="text-xs font-bold text-slate-900">Jan de Vries</p>
                                                <p className="text-[10px] text-slate-400">Hiring Manager</p>
                                             </div>
                                          </div>
                                          <div className="space-y-2">
                                             <a href="mailto:jan@client.com" className="flex items-center gap-2 text-xs text-slate-600 hover:text-red-0"><Mail size={12} /> jan@{client?.name.toLowerCase().replace(/\s/g, '')}.com</a>
                                             <a href="tel:+31612345678" className="flex items-center gap-2 text-xs text-slate-600 hover:text-red-0"><Phone size={12} /> +31 6 1234 5678</a>
                                          </div>
                                       </div>
                                    )}
                                 </div>
                              </div>
                           </div>
                        )
                     })}
                  </div>
               </div>
            )}


            {/* INSIGHTS VIEW */}
            {activeTab === 'insights' && (
               <div className="flex items-center justify-center h-full text-slate-300 flex-col gap-4">
                  <BarChart2 size={48} className="opacity-20" />
                  <p className="text-xs font-black uppercase tracking-widest">Performance dashboards coming soon</p>
               </div>
            )}

         </div>

         {selectedTodoId && (
            <TodoDetailModal
               todoId={selectedTodoId}
               onClose={() => setSelectedTodoId(null)}
               onUpdate={fetchUserAndTasks}
            />
         )}

         
           {outboxModalState.isOpen && outboxModalState.type === 'email' && (
              <BulkEmailComposerModal
                 isOpen={true}
                 onClose={() => setOutboxModalState({ isOpen: false, type: null, candidate: null, todoId: null, initialBody: '' })}
                 selectedCandidates={outboxModalState.candidate ? [outboxModalState.candidate] : []}
                 initialBody={outboxModalState.initialBody}
                 onSuccessCallback={async () => {
                    if (outboxModalState.todoId) {
                        await supabase.from('todos').update({ status: 'Completed' }).eq('id', outboxModalState.todoId);
                        fetchUserAndTasks();
                    }
                 }}
              />
           )}
       
           {outboxModalState.isOpen && outboxModalState.type === 'whatsapp' && (
              <BulkWhatsAppComposerModal
                 isOpen={true}
                 onClose={() => setOutboxModalState({ isOpen: false, type: null, candidate: null, todoId: null, initialBody: '' })}
                 selectedCandidates={outboxModalState.candidate ? [outboxModalState.candidate] : []}
                 initialBody={outboxModalState.initialBody}
                 onSuccessCallback={async () => {
                    if (outboxModalState.todoId) {
                        await supabase.from('todos').update({ status: 'Completed' }).eq('id', outboxModalState.todoId);
                        fetchUserAndTasks();
                    }
                 }}
              />
           )}

         {showTeamsModal && (
            <TeamsMeetingScheduler
               onClose={() => setShowTeamsModal(false)}
               onScheduled={() => {
                  window.dispatchEvent(
                     new CustomEvent('wt:toast', {
                        detail: { message: 'Afspraak succesvol verzonden via Microsoft Teams!', type: 'success' },
                     }),
                  );
                  setShowTeamsModal(false);
                  fetchUserAndTasks();
               }}
            />
         )}
      </div >
   );
};

export default RecruiterWorkspace;
